import React, { useEffect, useState,useRef } from "react";
import axios from "axios";
import Loader from '../loader';
import { toast } from "react-toastify";
const useFetchProfiles = (handleAddProfile, selectedPlatforms) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Track profiles that have already been fetched
  const fetchedProfilesRef = useRef(new Set());

  const allPlatforms = ["facebook", "instagram", "twitter", "tiktok"];
  // Use useEffect to trigger fetchProfiles on page load or refresh
  useEffect(() => {
    fetchProfiles();
    const interval = setInterval(fetchProfiles, 30000);
    return () => clearInterval(interval);
  }, [selectedPlatforms]); // Added selectedPlatforms as a dependency
  
  // Fetch profiles function
  const fetchProfiles = async () => {
    const platformsToFetch = (Array.isArray(selectedPlatforms) && selectedPlatforms.length > 0)
      ? selectedPlatforms
      : allPlatforms;
  
    setLoading(true);
  
    try {
      let allProfiles = [];
  
      const fetchPromises = platformsToFetch.map((platform) =>
        axios
          .get(`api/${platform}/fetch-all-profiles/`)
          .then((response) => {
            // Attach platform info to each profile
            const profilesWithPlatform = response.data.map(profile => ({
              ...profile,
              platform,
            }));
  
            allProfiles = [...allProfiles, ...profilesWithPlatform];
          })
          .catch((err) => {
            console.error(`Error fetching profiles from ${platform}:`, err);
            toast.error(err.message || `Error fetching profiles from ${platform}`);
          })
      );
  
      await Promise.all(fetchPromises);
  
      // 🔃 Now sort the combined profiles by entry_time
      const sortedProfiles = allProfiles.sort((a, b) => new Date(b.entry_time) - new Date(a.entry_time));
  
     
  
      // ✅ Now process sorted profiles
      if (handleAddProfile) {
        sortedProfiles.forEach((profileItem) => {
          const profileKey = `${profileItem.platform}-${profileItem.username}`;
  
          if (!fetchedProfilesRef.current.has(profileKey)) {
            fetchedProfilesRef.current.add(profileKey);
            handleAddProfile(profileItem, profileItem.platform);
          }
        });
      }
    } catch (err) {
      console.error("Error in fetching profiles:", err);
      toast.error(err.message || `Error fetching profiles`);
    } finally {
      setLoading(false);
    }
  };
  

  

  return { fetchProfiles, loading, error };
};

const ProfilingFilterBar = ({ onAddProfile }) => {
  const [platformOptions, setPlatformOptions] = useState([]);
  const [username, setUsername] = useState("");
  const [isAddingProfile, setIsAddingProfile] = useState(false);
  const platform = "twitter"; // Default platform
  

  // Move `handleAddProfile` above `useFetchProfiles`
  const handleAddProfile = async (profileItem, platform) => {
    let profile = {};
  
    // Common fields for all platforms
    profile = {
      platform: platform.toLowerCase(),
      username: profileItem.username,
      full_name: profileItem.full_name || profileItem.name,  // Use `full_name` or `name` if available
      description: profileItem.description || profileItem.biography,  // Use the appropriate field name
      location: profileItem.location || profileItem.current_city || profileItem.address ||profileItem.based_in ||'Not Available', // Handle different location fields
      stats: {
        posts: profileItem.statuses_count || profileItem.video_count || profileItem.posts_count || 'N/A',
        followers: profileItem.followers_count || profileItem.follower_count || 'N/A',
        friends: profileItem.friends_count || profileItem.followings_count || profileItem.following_count || 0,
      },
      email: profileItem.public_email || profileItem.biography_link || profileItem.email ||  "N/A",
      imgSrc: profileItem.profile_image_url || profileItem.profile_avatar_url || profileItem.profile_photo_url ||profileItem.avatar_url , // Use the correct profile image URL
      posts: profileItem.posts || [],  // Assuming `posts` is common across platforms
      followers: profileItem.followers || [],
      following: profileItem.following || [],
    };
  
    // Platform-specific adjustments
    switch (platform.toLowerCase()) {
      case 'twitter':
        // Twitter-specific fields
        profile.profile_banner_url = profileItem.profile_banner_url;
        profile.verified = profileItem.verified;
        profile.created_time = profileItem.created_time;
        break;
        
      case 'tiktok':
        // TikTok-specific fields
        profile.signature = profileItem.signature;
        profile.heart_count = profileItem.heart_count;
        profile.digg_count = profileItem.digg_count;
        profile.is_private = profileItem.is_private;
        profile.is_verified = profileItem.is_verified;
        break;
        
      case 'instagram':
        // Instagram-specific fields
        profile.profile_photo_url_hd = profileItem.profile_photo_url_hd;
        profile.is_business_account = profileItem.is_business_account;
        profile.is_verified = profileItem.is_verified;
        profile.gender = profileItem.gender;
        // profile.email = profileItem.public_email
        break;
        
      case 'facebook':
        // Facebook-specific fields
        profile.profile_cover_photo_url = profileItem.profile_cover_photo_url;
        profile.social_media_urls = profileItem.social_media_urls;
        profile.is_verified = profileItem.is_verified;
        profile.phone = profileItem.phone;
        break;
  
      default:
        break;
    }
  

    onAddProfile(profile);
  };
  

  const { loading, error } = useFetchProfiles(handleAddProfile, platform);

  const fetchProfileData = async (platform, username) => {
    
    try {
    
      const endpoint = `api/${platform}/check-profile/`;
  
      const response = await axios.post(
        endpoint,
        { username },
        {
          headers: { "Content-Type": "application/json" },
        }
      );
  
      return response.data; // ✅ Return the full response object
    } catch (error) {
      console.error("Error fetching profile data:", error);
      return null;
    }
  };
  

  const addProfileManually = async () => {
    if (platformOptions.length > 0 && username) {
      setIsAddingProfile(true);
  
      for (const platform of platformOptions) {
        const response = await fetchProfileData(platform, username);
  
        if (response) {
          if (response.exists) {
            toast.error(`The profile for ${username} on ${platform} already exists in the database.`);
          } else if (response.profile) {
            handleAddProfile(response.profile, platform); // ✅ Only add if `exists` is false
          }
        } else {
          toast.error(`No matching user found in database for ${platform}! Fetching from API.`);
        }
      }
  
      setIsAddingProfile(false);
      setUsername("");
      setPlatformOptions([]);
    } else {
      toast.error("Please select a platform and enter a username");
    }
  };
  
  


  return (
    <div className="p-filter-bar">
      <div className="add-profile-section">
        <input
          type="text"
          placeholder="Enter Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />

        {/* Platform Selection Icons */}
        <div className="platform-options" style={{ display: "flex", gap: "10px" }}>
          {[
            { name: "facebook", icon: "/icons/facebook.png" },
            { name: "instagram", icon: "/icons/instagram.png" },
            { name: "twitter", icon: "/icons/twitter.png" },
            { name: "tiktok", icon: "/icons/tiktok.png" },
          ].map((platform) => (
            <label key={platform.name} style={{ cursor: "pointer" }}>
              <input
                type="checkbox"
                value={platform.name}
                onChange={(e) =>
                  setPlatformOptions((prev) =>
                    prev.includes(platform.name)
                      ? prev.filter((p) => p !== platform.name)
                      : [...prev, platform.name]
                  )
                }
                checked={platformOptions.includes(platform.name)}
                style={{ display: "none" }}
              />
              <img
                src={platform.icon}
                alt={platform.name}
                style={{
                  width: "40px",
                  height: "40px",
                  opacity: platformOptions.includes(platform.name) ? 1 : 0.6,
                  transition: "opacity 0.3s",
                  borderRadius: "50%",
                  border: platformOptions.includes(platform.name)
                    ? "2px solid #3498db"
                    : "2px solid transparent",
                }}
              />
            </label>
          ))}
        </div>

        <button id="addProfile" onClick={addProfileManually} disabled={isAddingProfile}>
          {isAddingProfile ? "Loading..." : "Add Profile"}
        </button>
      </div>

    
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
    </div>
  );
};

export default ProfilingFilterBar;
